import { useState } from "react";
import { requestOtp, verifyOtp, resetPassword } from "../services/authService";

export const ForgotPasswordSteps = {
  EMAIL: 1,
  OTP: 2,
  RESET: 3,
  SUCCESS: 4,
};

// Client-side validations
function validateEmail(email) {
  if (!email) {
    return "Email is required.";
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return "Please enter a valid email address.";
  }
  return null;
}

function validateOtp(otp) {
  if (!otp) {
    return "OTP is required.";
  }
  if (!/^\d{6}$/.test(otp)) {
    return "OTP must be exactly 6 digits.";
  }
  return null;
}

function validatePasswords(password, confirmPassword) {
  if (!password) {
    return "New password is required.";
  }
  if (password.length < 8) {
    return "Password must be at least 8 characters long.";
  }
  if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one uppercase letter.";
  }
  if (!/[0-9]/.test(password)) {
    return "Password must contain at least one number.";
  }
  if (password !== confirmPassword) {
    return "Passwords do not match.";
  }
  return null;
}

/**
 * Custom React Hook that manages Forgot Password states, validations,
 * and coordinates directly with authService APIs.
 */
export function useForgotPassword(onSuccessRedirect) {
  const [step, setStep] = useState(ForgotPasswordSteps.EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");

  const resetFlow = () => {
    setStep(ForgotPasswordSteps.EMAIL);
    setEmail("");
    setOtp("");
    setPassword("");
    setConfirmPassword("");
    setError("");
    setLoading(false);
    setResetToken("");
  };

  const handleRequestOtp = async () => {
    setError("");
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const result = await requestOtp(email.trim());
      if (result.success) {
        setStep(ForgotPasswordSteps.OTP);
      } else {
        setError(result.message || "Failed to request OTP.");
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    const validationError = validateOtp(otp);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const result = await verifyOtp(email.trim(), otp.trim());
      if (result.success) {
        setResetToken(result.data?.reset_token || "");
        setStep(ForgotPasswordSteps.RESET);
      } else {
        setError(result.message || "Failed to verify OTP.");
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError("");
    const validationError = validatePasswords(password, confirmPassword);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const result = await resetPassword({
        email: email.trim(),
        otp: otp.trim(),
        resetToken,
        newPassword: password,
        confirmPassword,
      });

      if (result.success) {
        setStep(ForgotPasswordSteps.SUCCESS);
        if (onSuccessRedirect) {
          setTimeout(() => {
            onSuccessRedirect();
            resetFlow();
          }, 2000);
        }
      } else {
        setError(result.message || "Failed to reset password.");
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return {
    step,
    email,
    otp,
    password,
    confirmPassword,
    error,
    loading,
    setEmail,
    setOtp,
    setPassword,
    setConfirmPassword,
    resetFlow,
    handleRequestOtp,
    handleVerifyOtp,
    handleResetPassword,
  };
}
